import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { ReviewData } from '~/types'
import styles from './ProductDashboardMiniReviewSummary.module.scss'
import { REVIEWS_SECTION_ID } from '../ProductDashboardReviews'

const STAR_COUNT = 5

interface MiniStarRatingProps {
  rating: number
}

const MiniStarRating = ({ rating }: MiniStarRatingProps) => {
  const filledStars = Math.ceil(rating)

  return (
    <div
      className={styles.stars}
      aria-label={`${rating} out of ${STAR_COUNT} stars`}>
      {Array.from({ length: STAR_COUNT }, (_, i) => {
        const fillPct = i < filledStars ? 100 : 0

        return (
          <span key={i} className={styles.star} aria-hidden='true'>
            <svg
              className={styles.starEmpty}
              width={16}
              height={16}
              viewBox='0 0 24 24'>
              <path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' />
            </svg>
            <span className={styles.starFill} style={{ width: `${fillPct}%` }}>
              <svg width={16} height={16} viewBox='0 0 24 24'>
                <path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' />
              </svg>
            </span>
          </span>
        )
      })}
    </div>
  )
}

export interface ProductDashboardMiniReviewSummaryProps {
  reviewData?: ReviewData
}

export const ProductDashboardMiniReviewSummary = ({
  reviewData,
}: ProductDashboardMiniReviewSummaryProps) => {
  const { averageRating: rating, total } = reviewData ?? {}
  const totalReviews = total ?? 0

  const scrollToReviews = () => {
    const reviewsSection = document.getElementById(REVIEWS_SECTION_ID)

    if (!reviewsSection) {
      return
    }

    reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (totalReviews === 0) {
    return (
      <div className={styles.root}>
        <MiniStarRating rating={rating ?? 0} />
        <Typography
          tag={TypographyTag.span}
          variant={TypographyVariant.BodySmall}
          className={styles.count}>
          No reviews yet
        </Typography>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <MiniStarRating rating={rating ?? 0} />
      <Typography
        tag={TypographyTag.span}
        variant={TypographyVariant.BodySmallBold}
        className={styles.rating}>
        {(rating ?? 0).toFixed(1)}
      </Typography>
      <Typography
        tag={TypographyTag.span}
        variant={TypographyVariant.BodySmall}
        className={styles.count}>
        ({totalReviews})
      </Typography>
      <button
        type='button'
        className={styles.readAll}
        onClick={() => {
          scrollToReviews()
        }}>
        <Typography
          tag={TypographyTag.span}
          variant={TypographyVariant.BodySmall}
          className={styles.readAllLabel}>
          Read all reviews
        </Typography>
      </button>
    </div>
  )
}
